export const toArray = (value) => Array.isArray(value) ? value : value ? [value] : [];

/**
 * Normaliza un nivel de standings: acepta arrays, un único objeto o un objeto
 * indexado. `itemKeys` identifica si el objeto ya representa un elemento.
 */
export const toCollection = (value, itemKeys = []) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  if (itemKeys.some((key) => Object.prototype.hasOwnProperty.call(value, key))) {
    return [value];
  }
  return Object.values(value).filter((entry) => entry && typeof entry === "object");
};

/** Selecciona la primera estructura candidata con contenido real. */
export const firstNonEmpty = (...candidates) => candidates.find((candidate) =>
  Array.isArray(candidate) ? candidate.length > 0 : candidate && typeof candidate === "object" && Object.keys(candidate).length > 0
) || null;

export const createItem = (text, image = "") => text ? { text, image } : null;

export const createLeagueItem = (league) => {
  if (!league?.name && league?.id == null) return null;
  return createItem(`${league?.name || "Liga"} `);
};

/** Muestra rank y rankStatus, incluidos los aliases usados por algunos standings. */
export const formatRank = (entry, fallbackRank) => {
  const rank = entry?.rank ?? entry?.division?.rank ?? entry?.position ?? fallbackRank;
  const rankStatus = entry?.rankStatus ?? entry?.division?.rankStatus ?? entry?.rank_status ?? entry?.division?.rank_status;
  return `${rank ?? "—"} · ${rankStatus ?? "—"}`;
};

/** Crea un standing estructurado para poder renderizar sus dos líneas por separado. */
export const createStandingItem = ({ entry, fallbackRank, image = "", name, points, pointsLabel = "PTS", secondary = "", stats = null }) => {
  const rank = entry?.rank ?? entry?.division?.rank ?? entry?.position ?? fallbackRank;
  const rankStatus = entry?.rankStatus ?? entry?.division?.rankStatus ?? entry?.rank_status ?? entry?.division?.rank_status;
  const displayName = name || "Equipo";
  const displayPoints = points ?? "—";
  // `stats` es una lista opcional de pares { label, value } que se renderiza con
  // la inicial entre paréntesis y el valor en rojo (ej. (W) 80 (L) 80 (PCT) .544).
  const statList = Array.isArray(stats) ? stats.filter((stat) => stat && stat.value != null && stat.value !== "") : null;
  const statsText = statList?.length ? statList.map((stat) => `(${stat.label}) ${stat.value}`).join("  ") : displayPoints;

  return {
    type: "standing",
    text: `${rank ?? "—"} ${rankStatus ?? "—"} ${displayName} ${statsText}`,
    rank: rank ?? "—",
    rankStatus: rankStatus ?? "—",
    image,
    name: displayName,
    points: displayPoints,
    pointsLabel,
    secondary,
    stats: statList,
  };
};

/** Identifica la fase o grupo al que pertenecen los siguientes standings. */
export const createStandingPhaseItem = (phase) => ({
  type: "standing-phase",
  text: `${phase}`,
  phase,
});

const firstValue = (source, keys) => keys.map((key) => source?.[key]).find((value) => value != null && value !== "");
const asName = (value) => typeof value === "object" ? value?.name || value?.displayName || value?.shortName : value;
const formatDate = (value) => value ? new Date(value).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

/** Formatea encuentros por equipos y admite los aliases de cada proveedor deportivo. */
export function formatTeamMatches(matches, leagueName, icon, label, isResult) {
  return toArray(matches).map((match) => {
    const left = asName(firstValue(match, ["left_name", "home_name", "homeTeam", "home_team", "local"]));
    const right = asName(firstValue(match, ["right_name", "away_name", "awayTeam", "away_team", "visitor"]));
    if (!left || !right) return null;
    const leftScore = firstValue(match, ["left_score", "home_score", "homeScore", "local_score"]);
    const rightScore = firstValue(match, ["right_score", "away_score", "awayScore", "visitor_score"]);
    const detail = isResult && leftScore != null && rightScore != null ? ` ${leftScore} - ${rightScore} ` : " vs ";
    const date = !isResult ? formatDate(firstValue(match, ["utc_date", "startDate", "date"])) : "";
    return createItem(`${icon} ${label} · ${leagueName}: ${left}${detail}${right}${date ? ` · ${date}` : ""}`, firstValue(match, ["left_image", "home_image", "homeImage", "image"]) || "");
  }).filter(Boolean);
}

/** Formatea eventos individuales, como motorsport y tenis. */
export function formatIndividualMatches(matches, leagueName, icon, label, isResult) {
  return toArray(matches).map((match) => {
    const name = firstValue(match, ["winner_name", "contestantName", "player_name", "driver_name", "name", "event_name"]);
    if (!name) return null;
    const detail = firstValue(match, isResult ? ["position", "points", "score"] : ["utc_date", "startDate", "date"]);
    return createItem(`${icon} ${label} · ${leagueName}: ${name}${detail != null ? ` · ${isResult ? detail : formatDate(detail)}` : ""}`, firstValue(match, ["image", "contestantImage"]) || "");
  }).filter(Boolean);
}

export const isCurrentDateInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  try {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const normalizeDate = (dateStr) => {
      if (dateStr.endsWith("Z") && !dateStr.includes("T")) {
        return dateStr.replace("Z", "T00:00:00Z");
      }
      return dateStr;
    };
    const start = new Date(normalizeDate(startDate));
    const end = new Date(normalizeDate(endDate));

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

    return today >= start && today <= end;
  } catch (error) {
    console.warn("Error al verificar rango de fechas:", error);
    return false;
  }
};

/** Ventana previa (en ms) al primer partido de upcoming para alternar de resultados a próximos. */
export const UPCOMING_SWITCH_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Obtiene la fecha del evento admitiendo los aliases de cada proveedor. */
export const getEventDate = (event) => {
  const raw = firstValue(event, ["utc_date", "startDate", "date"]);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Fecha del primer partido (el más próximo) dentro de un arreglo de upcoming. */
export const getFirstUpcomingDate = (upcoming) => {
  const dates = toArray(upcoming)
    .map(getEventDate)
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  return dates[0] || null;
};

/**
 * Decide si se deben mostrar los próximos partidos en lugar de los resultados.
 * Devuelve true cuando faltan 24 horas o menos para el primer partido de upcoming.
 */
export const shouldShowUpcoming = (upcoming, now = new Date()) => {
  const firstDate = getFirstUpcomingDate(upcoming);
  if (!firstDate) return false;
  return now.getTime() >= firstDate.getTime() - UPCOMING_SWITCH_WINDOW_MS;
};

export const getPhaseName = (stage, division, stageIndex, divisionIndex) => {
  const stageName = stage?.name || stage?.stageName || stage?.phaseName || stage?.phase_name || `Fase ${stageIndex + 1}`;
  const divisionName = division?.name || division?.divisionName || division?.groupName || division?.group_name;

  return divisionName && divisionName !== stageName
    ? `${stageName} · ${divisionName}`
    : stageName || `Fase ${stageIndex + 1}.${divisionIndex + 1}`;
};

/* ------------------------------------------------------------------ */
/* Utilidades específicas de MLB (sport_code BASE).                    */
/* El standing de baseball usa la jerarquía:                           */
/* baseballStages[] → conferences[] (AL/NL) → divisions[] → teams[].   */
/* ------------------------------------------------------------------ */

/** Selecciona la primera estructura de stages de baseball con contenido. */
export const getBaseballStages = (standing) => {
  const source = firstNonEmpty(
    standing?.baseballStanding,
    standing?.baseballStages,
    standing?.baseball_standing,
    standing?.baseball_stages,
    standing?.stages
  );
  const stages = toCollection(source, ["conferences", "conference"]);
  if (stages.length) return stages;

  // Fallback: algunos endpoints entregan las conferencias (o divisiones)
  // directamente en la raíz del standing, sin el nivel intermedio de stages.
  if (standing?.conferences || standing?.conference || standing?.divisions || standing?.division) {
    return [standing];
  }
  return [];
};

/** Normaliza las conferencias (ligas) de un stage de baseball. */
export const getBaseballConferences = (stage) =>
  toCollection(firstNonEmpty(stage?.conferences, stage?.conference), ["divisions", "division"]);

/** Normaliza las divisiones de una conferencia de baseball. */
export const getBaseballDivisions = (conference) =>
  toCollection(firstNonEmpty(conference?.divisions, conference?.division), ["teams", "team"]);

/** Normaliza los equipos de una división de baseball. */
export const getBaseballTeams = (division) =>
  toCollection(firstNonEmpty(division?.teams, division?.team), ["teamId", "abbreviation", "record"]);

/** Nombre visible de la fase para el standing de MLB: "American League · East". */
export const getBaseballPhaseName = (conference, division) => {
  const conferenceName = conference?.abbreviation || conference?.name || "";
  const divisionName = division?.abbreviation || division?.name || "";
  return [conferenceName, divisionName].filter(Boolean).join(" · ") || "División";
};

/** Récord ganados-perdidos de un equipo de baseball. */
export const getBaseballRecord = (team) => {
  const wins = team?.record?.wins ?? team?.wins ?? "—";
  const losses = team?.record?.losses ?? team?.losses ?? "—";
  return `${wins}-${losses}`;
};

/** Nombre corto/visible de un equipo de baseball. */
export const getBaseballTeamName = (team) =>
  team?.abbreviation || team?.nickname || team?.location || team?.name || "Equipo";

/** Extrae los datos de baseball de un encuentro admitiendo aliases. */
export const getBaseballMatchData = (match) =>
  match?.baseball_data ?? match?.baseballData ?? match?.BaseballData ?? {};

/** Totales de línea (carreras, hits, errores) por lado del encuentro. */
export const getBaseballLineTotals = (stats) => ({
  runs: stats?.linescoreTotals?.runs ?? stats?.runs ?? "0",
  hits: stats?.linescoreTotals?.hits ?? stats?.hits ?? "0",
  errors: stats?.linescoreTotals?.errors ?? stats?.errors ?? "0",
});

/**
 * Récord ganados-perdidos de temporada del equipo dentro de un encuentro.
 * Viene en baseball_data.{left,right}_stats.record. Devuelve "" si no aplica.
 */
export const getBaseballTeamRecord = (stats) => {
  const record = stats?.record ?? {};
  const wins = record.wins ?? stats?.wins;
  const losses = record.losses ?? stats?.losses;
  if (wins == null && losses == null) return "";
  return `${wins ?? 0}-${losses ?? 0}`;
};

/** Nombre compacto del pitcher probable ("J. Matthews"). */
export const getPitcherName = (stats) => {
  const pitcher = stats?.start_pitcher ?? stats?.startPitcher;
  const player = pitcher?.player;
  if (!player) return "";
  const first = player.firstName ? `${player.firstName.charAt(0)}. ` : "";
  return `${first}${player.lastName || ""}`.trim();
};

/**
 * Detecta el rol del pitcher según start_pitcher.type.name (win / lose / save).
 * Devuelve la etiqueta corta correspondiente o "" si no aplica.
 */
export const getPitcherDecision = (stats) => {
  const type = (stats?.start_pitcher ?? stats?.startPitcher)?.type?.name;
  switch (String(type || "").toLowerCase()) {
    case "win": return "G";
    case "lose": return "P";
    case "save": return "S";
    default: return "";
  }
};

/** Récord y efectividad del pitcher probable ("9-9 · 4.69 ERA"), si existen. */
export const getPitcherRecord = (stats) => {
  const pitcher = stats?.start_pitcher ?? stats?.startPitcher;
  if (!pitcher) return "";
  const wins = pitcher.wins;
  const losses = pitcher.losses;
  const era = pitcher.earnedRunAverage;
  const hasRecord = typeof wins === "number" || typeof losses === "number";
  const hasEra = era && era !== "0.00" && era !== 0;
  const parts = [];
  if (hasRecord) parts.push(`${wins ?? 0}-${losses ?? 0}`);
  if (hasEra) parts.push(`${era} ERA`);
  return parts.join(" · ");
};

/* ------------------------------------------------------------------ */
/* Utilidades específicas de football americano (sport_code AMFO).     */
/* El standing de football usa la jerarquía:                           */
/* footballStanding[] → conferences[] (AFC/NFC) → divisions[] → teams[]. */
/* ------------------------------------------------------------------ */

/** Selecciona la primera estructura de stages de football con contenido. */
export const getAmericanFootballStages = (standing) => {
  const source = firstNonEmpty(
    standing?.footballStanding,
    standing?.football,
    standing?.football_standing,
    standing?.footballStages,
    standing?.american_stage,
    standing?.stages
  );
  const stages = toCollection(source, ["conferences", "conference"]);
  if (stages.length) return stages;

  // Fallback: algunos endpoints entregan las conferencias (o divisiones)
  // directamente en la raíz del standing, sin el nivel intermedio de stages.
  if (standing?.conferences || standing?.conference || standing?.divisions || standing?.division) {
    return [standing];
  }
  return [];
};

/** Normaliza las conferencias (AFC/NFC) de un stage de football. */
export const getAmericanFootballConferences = (stage) =>
  toCollection(firstNonEmpty(stage?.conferences, stage?.conference), ["divisions", "division"]);

/** Normaliza las divisiones de una conferencia de football. */
export const getAmericanFootballDivisions = (conference) =>
  toCollection(firstNonEmpty(conference?.divisions, conference?.division), ["teams", "team"]);

/** Normaliza los equipos de una división de football. */
export const getAmericanFootballTeams = (division) =>
  toCollection(firstNonEmpty(division?.teams, division?.team), ["teamId", "abbreviation", "record"]);

/** Récord ganados-perdidos de un equipo de football. */
export const getAmericanFootballRecord = (team) => {
  const wins = team?.record?.wins ?? team?.wins ?? "—";
  const losses = team?.record?.losses ?? team?.losses ?? "—";
  return `${wins}-${losses}`;
};

/** Nombre visible de un equipo de football (nickname con fallbacks). */
export const getAmericanFootballTeamName = (team) =>
  team?.nickname || team?.abbreviation || team?.location || team?.name || "Equipo";

/** Extrae los datos de football de un encuentro admitiendo aliases. */
export const getAmericanFootballMatchData = (match) =>
  match?.football_data ?? match?.footballData ?? match?.FootballData ?? {};

/**
 * Récord ganados-perdidos del equipo dentro de un encuentro de football.
 * Viene en football_data.{left,right}_stats.record. Devuelve "" si no aplica.
 */
export const getAmericanFootballTeamRecord = (stats) => {
  const record = stats?.record ?? {};
  const wins = record.wins ?? stats?.wins;
  const losses = record.losses ?? stats?.losses;
  if (wins == null && losses == null) return "";
  return `${wins ?? 0}-${losses ?? 0}`;
};
